import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { useSharedUserPreferences } from '@/contexts/UserPreferencesContext';

export type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: 'USD',
  setCurrency: () => {},
});

export const useCurrency = () => useContext(CurrencyContext);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { preferences, updatePreferences } = useSharedUserPreferences();
  
  // Guest currency state - initialized from localStorage
  const [guestCurrency, setGuestCurrency] = useState<Currency>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('guest_preferred_currency');
      if (stored && ['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(stored)) {
        return stored as Currency;
      }
    }
    return 'USD';
  });

  // Determine current currency based on auth state
  const currency: Currency = user 
    ? (preferences?.preferred_currency as Currency || 'USD')
    : guestCurrency;

  // Handle currency change
  const setCurrency = (newCurrency: Currency) => {
    if (user) {
      // Authenticated: persist to database
      updatePreferences({ preferred_currency: newCurrency });
    } else {
      // Guest: persist to localStorage and update state
      setGuestCurrency(newCurrency);
      if (typeof window !== 'undefined') {
        localStorage.setItem('guest_preferred_currency', newCurrency);
      }
    }
  };

  // Sync guest currency from localStorage on mount (handles multi-tab)
  useEffect(() => {
    if (!user && typeof window !== 'undefined') {
      const handleStorage = (e: StorageEvent) => {
        if (e.key === 'guest_preferred_currency' && e.newValue) {
          if (['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(e.newValue)) {
            setGuestCurrency(e.newValue as Currency);
          }
        }
      };
      window.addEventListener('storage', handleStorage);
      return () => window.removeEventListener('storage', handleStorage);
    }
  }, [user]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
};
